import { useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { useContext } from 'react';
import SessionContext from '../contexts/SessionContext';
import API_URL from '../constants/environment';
import { GetNotificationsResponse } from './useGetNotifications';

interface MarkNotificationReadInput {
    notificationId: string;
}

const markNotificationRead = async (data: MarkNotificationReadInput, sessionId: string | null) => {
    return axios.patch(`${API_URL}/notification/read`, { notificationId: data.notificationId }, {
        headers: {
            Authorization: sessionId
        }
    });
};

export const useMarkNotificationRead = () => {
    const sessionContext = useContext(SessionContext);
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (data: MarkNotificationReadInput) => markNotificationRead(data, sessionContext?.sessionId ?? null),
        onMutate: async (variables) => {
            await queryClient.cancelQueries({ queryKey: ['/notifications'] });

            let clearedNegotiationHandoff = false;
            const cachedLists = queryClient.getQueriesData<GetNotificationsResponse>({
                queryKey: ['/notifications'],
            });
            for (const [, data] of cachedLists) {
                const match = data?.notifications.find((n) => n._id === variables.notificationId);
                if (match?.type === 'negotiation-handoff' && !match.read) {
                    clearedNegotiationHandoff = true;
                    break;
                }
            }

            const previousNotifications = cachedLists;

            queryClient.setQueriesData<GetNotificationsResponse>(
                { queryKey: ['/notifications'] },
                (old) => {
                    if (!old) return old;
                    const wasUnread = old.notifications.some(
                        (n) => n._id === variables.notificationId && !n.read,
                    );
                    return {
                        ...old,
                        notifications: old.notifications.map((notification) => {
                            if (notification._id === variables.notificationId) {
                                return { ...notification, read: true };
                            }
                            return notification;
                        }),
                        unreadCount: wasUnread
                            ? Math.max(0, old.unreadCount - 1)
                            : old.unreadCount,
                    };
                },
            );

            // Negotiation badge / feed are owned by useMarkNegotiationRead — do not
            // optimistic-decrement unread-count here (avoids double-decrement on click).

            return { clearedNegotiationHandoff, previousNotifications };
        },
        onError: (_err, _vars, context) => {
            if (!context?.previousNotifications) return;
            for (const [key, data] of context.previousNotifications) {
                queryClient.setQueryData(key, data);
            }
        },
        onSuccess: (_data, _variables, context) => {
            void queryClient.invalidateQueries({ queryKey: ['/notifications'] });
            if (context?.clearedNegotiationHandoff) {
                void queryClient.invalidateQueries({ queryKey: ['/invoice/negotiations/feed'] });
                void queryClient.invalidateQueries({ queryKey: ['/invoice/negotiations/unread-count'] });
            }
        },
    });
};
